import React, { useMemo } from 'react';
import { Card, CardContent } from '../../../components/ui/card';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import {
  Trophy, Search, Users, ChevronDown, ChevronUp, Medal,
  Clock, User, MapPin, Loader2, Share2, Timer, Camera, Video,
} from 'lucide-react';
import PageTemplate from '../../../components/layout/PageTemplate';
import { DataTable } from '../../../components/shared';
import { RaceResultsViewProps } from './raceresultspage.props';

// ── Sub-components ──────────────────────────────────────────────────────────

function PodiumDisplay({ results }) {
  if (!results || results.length < 3) return null;
  const top3 = results.slice(0, 3);
  const podiumOrder = [top3[1], top3[0], top3[2]];
  const heights = ['h-24 sm:h-32', 'h-32 sm:h-44', 'h-20 sm:h-24'];
  const delays = ['animate-delay-200', '', 'animate-delay-400'];
  const medals = ['🥈', '🥇', '🥉'];
  const bgColors = ['bg-gradient-to-t from-gray-300 to-gray-200', 'bg-gradient-to-t from-yellow-400 to-amber-300', 'bg-gradient-to-t from-amber-700 to-amber-600'];

  return (
    <div className="flex items-end justify-center gap-2 sm:gap-4 py-6 px-4">
      {podiumOrder.map((r, i) => (
        <div key={r?.id || i} className={`flex flex-col items-center animate-podium-rise ${delays[i]}`}
             style={{ animationDelay: `${i * 150}ms` }}>
          {r && (
            <>
              <div className="text-3xl sm:text-4xl mb-1">{medals[i]}</div>
              <div className="text-center mb-2">
                <div className="font-bold text-sm sm:text-base truncate max-w-[100px] sm:max-w-[140px]">
                  {r.fullName}
                </div>
                <div className="text-xs sm:text-sm text-muted-foreground font-mono">
                  {r.chipTime || r.gunTime || '-'}
                </div>
                {r.club && (
                  <div className="text-xs text-muted-foreground truncate max-w-[100px]">
                    {r.club}
                  </div>
                )}
              </div>
              <div className={`${bgColors[i]} ${heights[i]} w-20 sm:w-28 rounded-t-lg flex items-end justify-center pb-2 shadow-lg transition-all duration-700`}>
                <span className="text-white font-bold text-lg sm:text-2xl">{i === 1 ? '1' : i === 0 ? '2' : '3'}</span>
              </div>
            </>
          )}
        </div>
      ))}
    </div>
  );
}

function RankBadge({ rank }) {
  if (!rank) return <span className="w-8 text-center text-muted-foreground">-</span>;
  if (rank <= 3) {
    const colors = ['', 'bg-amber-400 text-white', 'bg-gray-400 text-white', 'bg-amber-700 text-white'];
    return (
      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-sm ${colors[rank]}`}>
        {rank}
      </span>
    );
  }
  return <span className="w-8 text-center font-medium text-muted-foreground">{rank}</span>;
}

function TeamsView({ teams, expandedRow, setExpandedRow, rr = {} }: { teams: any[]; expandedRow: any; setExpandedRow: (v: any) => void; rr?: Record<string, any> }) {
  if (teams.length === 0) {
    return (
      <div className="text-center py-16 text-muted-foreground">
        <Users className="h-12 w-12 mx-auto mb-3 opacity-30" />
        <p>{rr.noTeamResults || 'No team results'}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {teams.map(t => (
        <Card key={t.id} className="overflow-hidden">
          <div
            className="flex items-center justify-between p-4 cursor-pointer hover:bg-muted/30 transition-colors"
            onClick={() => setExpandedRow(expandedRow === `t-${t.id}` ? null : `t-${t.id}`)}
          >
            <div className="flex items-center gap-3">
              <RankBadge rank={t.teamRank} />
              <div>
                <div className="font-bold">{t.teamName}</div>
                <div className="text-sm text-muted-foreground flex items-center gap-2">
                  <Clock className="h-3 w-3" /> {t.totalTime || '-'}
                  <span>·</span>
                  <Users className="h-3 w-3" /> {t.memberCount} {rr.members || 'members'}
                </div>
              </div>
            </div>
            {expandedRow === `t-${t.id}` ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
          </div>
          {expandedRow === `t-${t.id}` && t.members?.length > 0 && (
            <div className="border-t">
              <table className="w-full text-sm">
                <tbody>
                  {t.members.map(m => (
                    <tr key={m.id} className="border-b last:border-b-0 hover:bg-muted/20">
                      <td className="px-4 py-2 font-mono text-muted-foreground w-16">{m.bibNumber}</td>
                      <td className="px-4 py-2 font-medium">{m.fullName}</td>
                      <td className="px-4 py-2 font-mono text-right">{m.chipTime || m.gunTime || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      ))}
    </div>
  );
}

function ResultDetail({ result: r, rr = {} }: { result: any; rr?: Record<string, any> }) {
  const splits = useMemo(() => {
    if (!r.splitsJson) return null;
    try { return JSON.parse(r.splitsJson); } catch { return null; }
  }, [r.splitsJson]);

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 text-sm">
      {r.city && <DetailField icon={<MapPin className="h-3 w-3" />} label={rr.city || 'City'} value={r.city} />}
      {r.club && <DetailField icon={<Users className="h-3 w-3" />} label={rr.club || 'Club'} value={r.club} />}
      {r.yearOfBirth && <DetailField icon={<User className="h-3 w-3" />} label={rr.yearOfBirth || 'Year of Birth'} value={r.yearOfBirth} />}
      {r.gunTime && <DetailField icon={<Clock className="h-3 w-3" />} label="Gun Time" value={r.gunTime} />}
      {r.chipTime && <DetailField icon={<Timer className="h-3 w-3" />} label="Chip Time" value={r.chipTime} />}
      {r.pace && <DetailField icon={<Clock className="h-3 w-3" />} label={rr.pace || 'Pace'} value={r.pace} />}
      {r.ageGroupName && <DetailField icon={<Medal className="h-3 w-3" />} label={rr.ageGroup || 'Age Group'} value={`${r.ageGroupName}${r.ageGroupRank ? ` (#${r.ageGroupRank})` : ''}`} />}
      {r.teamName && <DetailField icon={<Users className="h-3 w-3" />} label={rr.team || 'Team'} value={r.teamName} />}
      {splits && (
        <div className="col-span-full">
          <div className="text-xs text-muted-foreground mb-1 flex items-center gap-1">
            <Clock className="h-3 w-3" /> {rr.laps || 'Laps'}
          </div>
          <div className="flex gap-2 flex-wrap">
            {splits.map((s, i) => (
              <Badge key={i} variant="outline" className="text-xs font-mono">{s}</Badge>
            ))}
          </div>
        </div>
      )}
      {(r.photoLink || r.videoLink) && (
        <div className="col-span-full flex gap-4 pt-1">
          {r.photoLink && (
            <a href={r.photoLink} target="_blank" rel="noopener noreferrer"
               className="text-blue-600 hover:underline text-sm flex items-center gap-1">
              📷 {rr.photo || 'Photo'}
            </a>
          )}
          {r.videoLink && (
            <a href={r.videoLink} target="_blank" rel="noopener noreferrer"
               className="text-blue-600 hover:underline text-sm flex items-center gap-1">
              🎥 {rr.video || 'Video'}
            </a>
          )}
        </div>
      )}
    </div>
  );
}

function DetailField({ icon, label, value }) {
  return (
    <div>
      <div className="text-xs text-muted-foreground flex items-center gap-1 mb-0.5">
        {icon} {label}
      </div>
      <div className="font-medium">{value}</div>
    </div>
  );
}

const STATUS_MAP = { 0: 'Finished', 1: 'DNF', 2: 'DNS', 3: 'DSQ' };

// ── Main View ───────────────────────────────────────────────────────────────

const RaceResultsView: React.FC<RaceResultsViewProps> = ({
  lang, setLang, t, rr,
  race, summary, results, teams, total, loading,
  expandedRow, setExpandedRow,
  raceTypeId, gender, ageGroup, search, view, page, totalPages,
  raceTypes, seriesRaces, raceId,
  resultColumns, updateParam, handleShare, navigate,
}) => {
  return (
    <PageTemplate lang={lang} setLang={setLang} t={t} forceScrolled={true}>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-900 to-indigo-900 text-white" style={{ paddingTop: 80 }}>
        <div className="max-w-7xl mx-auto px-4 py-4 sm:py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-wrap">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold">
                  {race?.raceSeries?.name || race?.name || rr.results || 'Results'}
                </h1>
                {race?.startDate && (
                  <p className="text-blue-200 text-sm">
                    {new Date(race.startDate).toLocaleDateString(lang === 'mk' ? 'mk-MK' : lang === 'al' ? 'sq-AL' : 'en-US', {
                      day: 'numeric', month: 'long', year: 'numeric'
                    })}
                  </p>
                )}
              </div>
              {/* Edition Picker */}
              {seriesRaces.length > 1 && (
                <div className="flex items-center gap-1">
                  <span className="text-blue-200 text-sm mr-1">|</span>
                  {seriesRaces.map(sr => (
                    <button
                      key={sr.id}
                      onClick={() => { if (sr.id !== raceId) navigate(`/results/${sr.id}?lang=${lang}`); }}
                      className={`px-2.5 py-1 rounded-full text-xs font-medium transition-colors ${
                        sr.id === raceId
                          ? 'bg-white/20 text-white'
                          : 'text-blue-200 hover:bg-white/10'
                      }`}
                    >
                      {new Date(sr.startDate).getFullYear()}
                    </button>
                  ))}
                </div>
              )}
              {/* Inline Stats */}
              {summary && summary.totalFinishers > 0 && (
                <div className="flex flex-wrap gap-2 items-center text-sm">
                  <span className="text-blue-200">|</span>
                  <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" /> {summary.totalFinishers} {rr.finished || 'finished'}</span>
                  {summary.maleCount > 0 && <span>· {summary.maleCount} {rr.male || 'Male'}</span>}
                  {summary.femaleCount > 0 && <span>· {summary.femaleCount} {rr.female || 'Female'}</span>}
                  {summary.fastestTime && <span className="flex items-center gap-1">· <Timer className="h-3.5 w-3.5" /> {summary.fastestTime}</span>}
                  {summary.totalTeams > 0 && <span>· {summary.totalTeams} {rr.teams || 'teams'}</span>}
                </div>
              )}
            </div>
            <Button variant="ghost" size="sm" className="text-white hover:bg-white/10" onClick={handleShare}>
              <Share2 className="h-4 w-4 mr-1" /> {rr.share || 'Share'}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* Podium */}
        {!search && view !== 'teams' && results.length >= 3 && page === 1 && (
          <Card className="mb-6 overflow-hidden">
            <CardContent className="p-0">
              <PodiumDisplay results={results} />
            </CardContent>
          </Card>
        )}

        {/* Race Type Pills */}
        {raceTypes.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-4">
            <Button
              variant={!raceTypeId ? 'default' : 'outline'}
              size="sm"
              onClick={() => updateParam('raceTypeId', '')}
            >
              {rr.all || 'All'}
            </Button>
            {raceTypes.map(rt => (
              <Button
                key={rt.id}
                variant={raceTypeId === String(rt.id) ? 'default' : 'outline'}
                size="sm"
                onClick={() => updateParam('raceTypeId', String(rt.id))}
              >
                {rt.name}
              </Button>
            ))}
          </div>
        )}

        {/* View Mode Tabs */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div className="flex gap-1 bg-white rounded-lg p-1 shadow-sm border">
            {['overall', 'ageGroups', 'teams'].map(v => (
              <button
                key={v}
                onClick={() => updateParam('view', v === 'overall' ? '' : v)}
                className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                  view === v ? 'bg-primary text-primary-foreground shadow-sm' : 'text-muted-foreground hover:bg-muted'
                }`}
              >
                {v === 'overall' ? (rr.overall || 'Overall') : v === 'ageGroups' ? (rr.ageGroups || 'Age Groups') : (rr.teamsTab || 'Teams')}
              </button>
            ))}
          </div>

          {/* Filters Row */}
          {view !== 'teams' && (
            <div className="flex flex-wrap gap-2 items-center">
              <div className="flex gap-1 bg-white rounded-lg border overflow-hidden">
                {['', 'M', 'F'].map(g => (
                  <button
                    key={g}
                    onClick={() => updateParam('gender', g)}
                    className={`px-3 py-1.5 text-sm font-medium transition-colors ${
                      gender === g ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-muted'
                    }`}
                  >
                    {g === '' ? (rr.all || 'All')
                     : g === 'M' ? `${rr.male || 'Male'}${summary?.maleCount ? ` (${summary.maleCount})` : ''}`
                     : `${rr.female || 'Female'}${summary?.femaleCount ? ` (${summary.femaleCount})` : ''}`}
                  </button>
                ))}
              </div>

              {summary?.ageGroups?.length > 0 && (
                <Select value={ageGroup || 'all'} onValueChange={v => updateParam('ageGroup', v === 'all' ? '' : v)}>
                  <SelectTrigger className="w-[180px] bg-white">
                    <SelectValue placeholder={rr.ageGroup || 'Age Group'} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{rr.allGroups || 'All Groups'}</SelectItem>
                    {summary.ageGroups.map(ag => (
                      <SelectItem key={ag} value={ag}>{ag}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <div className="relative min-w-[200px]">
                <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  className="pl-8 bg-white"
                  placeholder={rr.search || 'Search...'}
                  value={search}
                  onChange={e => updateParam('search', e.target.value)}
                />
              </div>
            </div>
          )}
        </div>

        {/* Results */}
        {loading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : view === 'teams' ? (
          <TeamsView teams={teams} expandedRow={expandedRow} setExpandedRow={setExpandedRow} rr={rr} />
        ) : (
          <>
            {/* Desktop Table */}
            <div className="hidden sm:block">
              <Card>
                <DataTable
                  columns={resultColumns}
                  data={results}
                  expandedRowId={expandedRow}
                  onExpandToggle={(id) => setExpandedRow(expandedRow === id ? null : id)}
                  renderExpanded={(r) => <ResultDetail result={r} rr={rr} />}
                  rowClassName={(r, idx) => idx < 3 && page === 1 && !search ? 'bg-amber-50/30' : ''}
                  headerClassName="border-b bg-muted/50"
                  emptyMessage={search ? (rr.noResultsSearch || 'No results for this search') : (rr.noResults || 'No results')}
                />
              </Card>
            </div>

            {/* Mobile Cards */}
            <div className="sm:hidden space-y-2">
              {results.map(r => (
                <Card key={r.id} className="overflow-hidden">
                  <div
                    className="p-3 cursor-pointer"
                    onClick={() => setExpandedRow(expandedRow === r.id ? null : r.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <RankBadge rank={r.overallRank} />
                        <div>
                          <div className="font-medium text-sm">{r.fullName}</div>
                          <div className="text-xs text-muted-foreground">
                            BIB {r.bibNumber} {r.club && `· ${r.club}`}
                          </div>
                        </div>
                      </div>
                      <div className="text-right flex items-center gap-1">
                        {r.photoLink && (
                          <a href={r.photoLink} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-blue-100 text-blue-600"
                             onClick={e => e.stopPropagation()}>
                            <Camera className="h-3.5 w-3.5" />
                          </a>
                        )}
                        {r.videoLink && (
                          <a href={r.videoLink} target="_blank" rel="noopener noreferrer"
                             className="inline-flex items-center justify-center w-7 h-7 rounded-full hover:bg-red-100 text-red-600"
                             onClick={e => e.stopPropagation()}>
                            <Video className="h-3.5 w-3.5" />
                          </a>
                        )}
                        <div>
                          <div className="font-mono font-medium text-sm">
                            {r.status === 0 ? (r.chipTime || r.gunTime || '-') : STATUS_MAP[r.status]}
                          </div>
                          {r.pace && <div className="text-xs text-muted-foreground font-mono">{r.pace}</div>}
                        </div>
                      </div>
                    </div>
                  </div>
                  {expandedRow === r.id && (
                    <div className="border-t px-3 py-2 bg-muted/20">
                      <ResultDetail result={r} rr={rr} />
                    </div>
                  )}
                </Card>
              ))}
            </div>

            {results.length === 0 && !loading && (
              <div className="text-center py-16 text-muted-foreground">
                <Trophy className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>{search ? (rr.noResultsSearch || 'No results for this search') : (rr.noResults || 'No results')}</p>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <span className="text-sm text-muted-foreground">
                  {(page - 1) * 50 + 1}-{Math.min(page * 50, total)} {rr.of || 'of'} {total}
                </span>
                <div className="flex gap-1">
                  <Button variant="outline" size="sm" disabled={page <= 1}
                    onClick={() => updateParam('page', String(page - 1))}>
                    ←
                  </Button>
                  <Button variant="outline" size="sm" disabled={page >= totalPages}
                    onClick={() => updateParam('page', String(page + 1))}>
                    →
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Podium Animation Styles */}
      <style>{`
        @keyframes podiumRise {
          from { transform: scaleY(0); opacity: 0; }
          to   { transform: scaleY(1); opacity: 1; }
        }
        .animate-podium-rise {
          animation: podiumRise 0.7s cubic-bezier(0.34, 1.56, 0.64, 1) forwards;
          transform-origin: bottom;
        }
      `}</style>
    </div>
    </PageTemplate>
  );
};

export default RaceResultsView;
